import { useTranslation } from "react-i18next";
import { X, Trash, Plus } from "lucide-react";
import ReactMarkdown from "react-markdown";
import {
  getGetUserSummarizersQueryKey,
  useDeleteSummarizer,
  useGetUserSummarizers,
} from "@packages/api/madlenAPI";
import { useEffect, useState } from "react";
import { useToast } from "@packages/contexts/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import Joyride, { CallBackProps, Step } from "react-joyride";
import { joyrideStyles, joyrideStylesDark } from "@packages/utils";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { useMobile } from "@packages/hooks/useMobile";
import { trackEvent } from "@packages/hooks/useAnalytics";
import SummaryCard from "../../components/summaries/SummaryCard";
import DeleteConfirmPopUp from "../../components/tool_components/DeleteConfirmPopUp";
import ErrorComponent from "../../components/tool_components/ErrorComponent";
import { Button, Text } from "@core/index";
import remarkGfm from "remark-gfm";
import ToolHelper from "@features/tools/components/ToolHelper";
import { useTheme } from "@packages/hooks/useTheme";

const SummariesView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [runTour, setRunTour] = useState(false);
  const [skipAllTours] = useLocalStorage("skip-all-tours", false);
  const isMobile = useMobile();
  const [selectedSummary, setSelectedSummary] = useState<any | null>(null);
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const { theme } = useTheme();
  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="flex flex-col items-center gap-4">
          <video
            src="/tutorials/summaries.mp4"
            autoPlay
            loop
            muted
            className="w-[350px] rounded-xl"
          />
          <Text variant={"p"} className="text-center">
            {t("onboarding.summaries.tour.welcome")}
          </Text>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".create-button",
      content: t("onboarding.summaries.tour.create"),
      placement: "bottom-end",
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;

    if (action === "close") {
      setRunTour(false);
      return;
    }

    if (status === "finished" || status === "skipped") {
      setRunTour(false);
      if (status === "finished") {
        navigate("/content/summaries/create");
      }
    }
  };

  const { addToast } = useToast();
  const {
    data: summaries,
    isLoading,
    error,
    refetch,
  } = useGetUserSummarizers();
  const queryClient = useQueryClient();
  const queryKey = getGetUserSummarizersQueryKey();
  const { mutate: deleteSummarizer } = useDeleteSummarizer({
    mutation: {
      onMutate: async ({ id }) => {
        const previousData = queryClient.getQueryData(queryKey);
        // Optimistically update UI
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData?.data?.filter((summary: any) => summary.id !== id),
          };
        });

        return { previousData };
      },
      onError: (error, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
        console.error("Failed to delete connection:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });
  const handleDelete = async (summaryId: number) => {
    try {
      await deleteSummarizer({ id: summaryId });
      addToast({
        title: t("summaries.deleted"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      addToast({
        title: t("summaries.deleteError"),
        description: t("summaries.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  useEffect(() => {
    const shouldShowTour =
      !isLoading &&
      (!summaries?.data || summaries.data.length === 0) &&
      !skipAllTours &&
      !isMobile;
    setRunTour(shouldShowTour);
  }, [isLoading, summaries?.data, skipAllTours, isMobile]);

  const renderModal = () =>
    selectedSummary && (
      <div className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-dark-600 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-dark-200 dark:border-dark-400">
          <div className="p-3 flex justify-end items-center border-b border-gray-200 dark:border-dark-400">
            <Button
              size="icon"
              rounded="full"
              onClick={() => setSelectedSummary(null)}
              bgColor="hover:bg-gray-100 dark:hover:bg-dark-700"
              className="w-10 h-10"
            >
              <X size={24} className="text-gray-500 dark:text-gray-400" />
            </Button>
          </div>

          <div className="flex-1 overflow-auto px-6 pb-6">
            <SummaryCard
              createdAt={selectedSummary.createdAt ?? ""}
              content={selectedSummary.markdownSummary}
              imageUrl={selectedSummary.imageUrl}
              videoUrl={selectedSummary.videoUrl}
              author={selectedSummary.authorName ?? ""}
              onCreateAgain={() => navigate("/content/summaries/create")}
            />
          </div>
        </div>
      </div>
    );

  const LoadingState = () => (
    <div
      className="flex flex-col items-center justify-center min-h-[400px] space-y-4"
      data-testid="loading-dot-container"
    >
      <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      <Text variant={"p"} className="text-gray-600 dark:text-gray-400">
        {t("summaries.loading")}
      </Text>
    </div>
  );

  const { trackEvent: track } = trackEvent();
  const handleClick = () => {
    track(
      {
        category: "ContentGeneration",
        action: "summary.generate",
        label: "Summary generation click",
        nonInteraction: false,
      },
      {
        location: "/summary/generate",
        element: "generate-button",
      },
    );
  };

  const textToDelete =
    summaries?.data
      ?.find((text) => text.id === showDeletePopup)
      ?.markdownSummary?.substring(0, 30)
      ?.replace(/\*/g, "")
      ?.replace(/\n/g, "")
      ?.replace(/#/g, "") || t("summaries.untitled");

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6 text-center">
      <div className="w-24 h-24 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
        <div className="w-12 h-12 bg-teal-500 dark:bg-teal-400 rounded-full flex items-center justify-center">
          <Plus size={24} className="text-white" />
        </div>
      </div>
      <div className="space-y-2">
        <Text variant="h3" className="text-gray-900 dark:text-gray-100">
          {t("summaries.empty.title")}
        </Text>
        <Text variant="p" className="text-gray-600 dark:text-gray-400 max-w-md">
          {t("summaries.empty.description")}
        </Text>
      </div>
      <Button
        onClick={() => navigate("/content/summaries/create")}
        size="lg"
        rounded="full"
        bgColor="bg-teal-500 hover:bg-teal-600 dark:bg-teal-500 dark:hover:bg-teal-600"
        textColor="text-white"
        className="create-button px-8"
      >
        {t("summaries.create.title")}
      </Button>
    </div>
  );

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <Joyride
        locale={{
          back: t("onboarding.back"),
          close: t("onboarding.close"),
          last: t("onboarding.finish"),
          next: t("onboarding.next"),
          skip: t("onboarding.skip"),
        }}
        steps={steps}
        run={!skipAllTours && !isMobile && runTour}
        continuous
        showSkipButton
        styles={theme === "dark" ? joyrideStylesDark : joyrideStyles}
        callback={handleJoyrideCallback}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t("quickActions.summaries")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t("quickActions.summariesDesc")}
            </Text>
          </div>
          <div className="flex-shrink-0">
            <Button
              onClick={() => {
                handleClick();
                navigate("/content/summaries/create");
              }}
              size="md"
              rounded="full"
              bgColor="bg-teal-500 hover:bg-teal-600 dark:bg-teal-500 dark:hover:bg-teal-600"
              textColor="text-white"
              className="create-button px-6 sm:px-8"
              leftIcon={<Plus size={20} />}
            >
              {t("summaries.create.title")}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <LoadingState />
          </div>
        ) : error ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <ErrorComponent
              title={t("summaries.error.title")}
              refetch={refetch}
              description={t("summaries.error.description")}
              retryButton={t("summaries.error.retryButton")}
            />
          </div>
        ) : !summaries?.data?.length ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <EmptyState />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {summaries?.data
              ?.filter((summary) => summary.markdownSummary)
              .map((summary) => (
                <div
                  key={summary.id}
                  className="group cursor-pointer bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 overflow-hidden transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="p-4 sm:p-6 h-full relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      rounded="xl"
                      className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-all duration-200"
                      bgColor="bg-white/90 dark:bg-dark-500/90 hover:bg-red-50 dark:hover:bg-red-900/50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeletePopup(summary.id);
                      }}
                    >
                      <Trash
                        size={16}
                        className="text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
                      />
                    </Button>

                    <div
                      onClick={() => setSelectedSummary(summary)}
                      className="h-full"
                    >
                      {(summary.imageUrl || summary.videoUrl) && (
                        <div className="relative h-32 sm:h-40 mx-auto rounded-lg overflow-hidden mb-4 bg-gray-100 dark:bg-dark-500">
                          {summary.imageUrl ? (
                            <img
                              src={summary.imageUrl}
                              alt="Summary illustration"
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                          ) : summary.videoUrl ? (
                            <video
                              src={summary.videoUrl}
                              className="w-full h-full object-cover"
                              muted
                            />
                          ) : null}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Text
                            variant="span"
                            className="text-xs text-gray-500 dark:text-gray-400"
                          >
                            {new Date(summary.createdAt).toLocaleDateString()}
                          </Text>
                          {summary.authorName && (
                            <Text
                              variant="span"
                              className="text-xs text-gray-500 dark:text-gray-400"
                            >
                              {summary.authorName}
                            </Text>
                          )}
                        </div>

                        <div className="prose dark:prose-invert max-w-none prose-sm">
                          {summary.markdownSummary && (
                            <div className="text-gray-700 dark:text-gray-300 line-clamp-4 overflow-hidden text-ellipsis transition-colors group-hover:text-gray-900 dark:group-hover:text-gray-100">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                className="prose dark:prose-invert max-w-none prose-headings:text-sm prose-headings:font-semibold prose-headings:mt-0 prose-headings:mb-1 prose-p:text-sm prose-p:mt-0 prose-p:mb-1 prose-p:leading-relaxed"
                              >
                                {summary.markdownSummary.substring(0, 200) +
                                  (summary.markdownSummary.length > 200
                                    ? "..."
                                    : "")}
                              </ReactMarkdown>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Modal */}
        {renderModal()}

        <ToolHelper
          toolPath={"summaries"}
          shouldShowTour={
            !skipAllTours &&
            !isMobile &&
            summaries?.data &&
            summaries?.data?.length === 0
          }
          toolType={t(`sidebar.menu.toolbox.summaries`)}
        />

        {/* Delete Confirmation PopUp */}
        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("summaries.confirmDelete", {
              title: textToDelete + "...",
            })}
            showDeletePopup={showDeletePopup}
            setShowDeletePopup={setShowDeletePopup}
            handleDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
};

export default SummariesView;
